import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Home20Regular,
  ChevronRight20Regular,
  ChevronDown20Regular,
  Add20Regular,
  Warning16Regular,
  Person20Regular,
  Save20Regular,
  CheckmarkCircle20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { ContactRefField } from "../components/relationsInline/ContactRefField";
import { type ContactRef } from "../components/relationsInline/types";
import { RELATIONSHIP_FREE } from "../utils/householdRole";
import { useHouseholdRelationLabels } from "../services/useHouseholdLists";
import {
  fetchRelationCluster,
  upsertClusterRelations,
  dismissCandidate,
  addClusterMember,
  genderKey,
  reverseRelationLabel,
  type RelationClusterDetail as ClusterDetail,
  type ClusterHousehold,
  type HouseholdMember,
  type HouseholdRelationRow,
} from "../services/haushalteCleanupService";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: "16px", paddingBottom: "80px" },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    alignSelf: "flex-start",
    border: "none",
    background: "transparent",
    color: BRAND.purple,
    fontSize: "0.9rem",
    cursor: "pointer",
    padding: "4px 0",
  },
  head: { display: "flex", flexDirection: "column", gap: "2px" },
  title: { fontFamily: BRAND.fontAccent, fontSize: "1.35rem", color: BRAND.ink },
  sub: { fontSize: "0.85rem", color: BRAND.muted },
  hhCard: {
    border: `1px solid ${BRAND.border}`,
    borderRadius: "12px",
    background: BRAND.cardBg,
    boxShadow: "0 1px 3px rgba(45,20,43,0.05)",
    overflow: "hidden",
  },
  hhHead: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 14px",
    background: "#faf7fa",
    borderBottom: `1px solid ${BRAND.borderDashed}`,
  },
  hhIcon: { color: BRAND.purple, display: "flex" },
  hhTitle: { fontWeight: 600, color: BRAND.ink, fontSize: "0.95rem" },
  hhSub: { fontSize: "0.8rem", color: BRAND.muted },
  memberRow: {
    display: "flex",
    flexDirection: "column",
    borderBottom: `1px solid ${BRAND.borderDashed}`,
  },
  memberHead: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 14px",
    cursor: "pointer",
    background: "transparent",
    border: "none",
    textAlign: "left",
    width: "100%",
  },
  memberName: { fontWeight: 500, color: BRAND.ink, fontSize: "0.9rem", flex: 1 },
  relCountChip: {
    fontSize: "0.72rem",
    color: BRAND.muted,
    background: "#f3eef3",
    borderRadius: "10px",
    padding: "1px 8px",
  },
  unclearDot: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    fontSize: "0.72rem",
    color: "#8a5a00",
    background: BRAND.gold + "22",
    borderRadius: "10px",
    padding: "1px 8px",
  },
  relList: { display: "flex", flexDirection: "column", gap: "8px", padding: "4px 14px 14px 34px" },
  relRow: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" },
  relOther: { fontSize: "0.85rem", color: BRAND.ink, minWidth: "150px" },
  relIs: { fontSize: "0.8rem", color: BRAND.muted },
  select: {
    padding: "5px 8px",
    borderRadius: "8px",
    border: `1px solid ${BRAND.border}`,
    fontSize: "0.82rem",
    color: BRAND.ink,
    background: BRAND.white,
    minWidth: "150px",
  },
  emptyRel: { fontSize: "0.8rem", color: BRAND.muted, fontStyle: "italic" },
  relFromTo: { fontSize: "0.8rem", color: BRAND.muted },
  /** Freitext-Beziehung: beide Richtungen untereinander, eingerückt unter dem Select. */
  freeRelBox: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    marginTop: "6px",
    marginLeft: "8px",
    paddingLeft: "10px",
    borderLeft: `2px solid ${BRAND.borderDashed}`,
    flexBasis: "100%",
  },
  freeRelRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
    fontSize: "0.82rem",
    color: BRAND.muted,
  },
  freeInput: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.86rem",
    padding: "6px 10px",
    borderRadius: "8px",
    border: `1px solid ${BRAND.borderDashed}`,
    minWidth: "170px",
    color: BRAND.ink,
  },
  miniAddBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    border: "none",
    background: "transparent",
    color: BRAND.purple,
    fontSize: "0.8rem",
    fontWeight: 500,
    cursor: "pointer",
    padding: "4px 0",
    marginTop: "2px",
  },
  addInline: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
    padding: "8px 10px",
    marginTop: "4px",
    background: "#faf7fa",
    borderRadius: "8px",
    border: `1px solid ${BRAND.borderDashed}`,
  },
  addInlinePicker: { minWidth: "220px", flex: "1 1 220px" },
  addWrap: { padding: "8px 14px 12px" },
  toggleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    border: `1px dashed ${BRAND.border}`,
    background: "transparent",
    color: BRAND.purple,
    fontSize: "0.82rem",
    cursor: "pointer",
    padding: "6px 10px",
    borderRadius: "8px",
  },
  sectionBox: {
    border: `1px solid ${BRAND.border}`,
    borderRadius: "12px",
    background: BRAND.cardBg,
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  sectionTitle: { fontFamily: BRAND.fontAccent, fontSize: "1rem", color: BRAND.ink },
  sectionHint: { fontSize: "0.8rem", color: BRAND.muted },
  crossRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    padding: "8px 0",
    borderBottom: `1px solid ${BRAND.borderDashed}`,
  },
  addBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    border: "none",
    background: BRAND.purple,
    color: BRAND.white,
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    padding: "7px 14px",
    borderRadius: "8px",
  },
  addBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  loading: { padding: "24px", textAlign: "center", color: BRAND.muted },
  err: { color: "#b3261e", fontSize: "0.82rem" },

  /** Entwurfs-Kennzeichnung an einer Zeile, deren Änderung noch nicht gespeichert ist. */
  draftChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    fontSize: "0.72rem",
    color: "#1c4f86",
    background: "#e3eefb",
    borderRadius: "10px",
    padding: "1px 8px",
  },
  /** Zeile im Block „Geplante Änderungen". */
  changeRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    fontSize: "0.84rem",
    color: BRAND.ink,
    padding: "6px 0",
    borderBottom: `1px solid ${BRAND.borderDashed}`,
  },
  changeText: { flex: 1, minWidth: "220px" },
  linkBtn: {
    border: "none",
    background: "transparent",
    color: BRAND.purple,
    fontSize: "0.8rem",
    cursor: "pointer",
    padding: "2px 0",
    textDecoration: "underline",
  },
  /** Sticky-Aktionsleiste am unteren Rand: speichern / abhaken. */
  actionBar: {
    position: "sticky",
    bottom: 0,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    padding: "12px 14px",
    background: BRAND.white,
    border: `1px solid ${BRAND.border}`,
    borderRadius: "12px",
    boxShadow: "0 -2px 12px rgba(45,20,43,0.08)",
    zIndex: 2,
  },
  actionInfo: { fontSize: "0.82rem", color: BRAND.muted, flex: 1, minWidth: "160px" },
  ghostBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    border: `1px solid ${BRAND.border}`,
    background: BRAND.white,
    color: BRAND.ink,
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    padding: "8px 14px",
    borderRadius: "8px",
  },
  /** Auswahl einer Cluster-Person (freier Beziehungs-Block). */
  personSelect: {
    padding: "6px 8px",
    borderRadius: "8px",
    border: `1px solid ${BRAND.border}`,
    fontSize: "0.85rem",
    color: BRAND.ink,
    background: BRAND.white,
    minWidth: "190px",
  },
  freeBlockRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
});

/**
 * Klärungsbedürftig ist NUR ein leeres Label oder der Import-Platzhalter „Verwandt".
 * Ein bewusst gesetzter Freitext (z. B. „Patentante") ist eine gewollte Beziehung und
 * gilt als geklärt — er wird nicht als unklar markiert.
 */
const isUnclearLabel = (label: string): boolean =>
  !label.trim() || label.trim() === "Verwandt";

const memberFullName = (m: HouseholdMember): string =>
  [m.firstName, m.name].filter(Boolean).join(" ").trim() || `Kontakt ${m.contactId}`;

/**
 * Anzeigename einer Picker-Auswahl. Ein per Suche gewählter Kontakt bringt nur
 * `displayName` mit („Nachname, Vorname"); die Einzelfelder sind erst bei einem
 * frei eingetippten Kontakt gefüllt. Ohne den displayName-Vorrang stünde in der
 * Änderungsliste bloß „Kontakt".
 */
const refName = (r: ContactRef | null | undefined): string =>
  (r?.displayName ?? "").trim() ||
  [r?.firstName, r?.lastName].filter(Boolean).join(" ").trim() ||
  "Kontakt";

const addrLine = (a: { strasse: string | null; plz: string | null; ort: string | null }): string =>
  [a.strasse, [a.plz, a.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ");

/**
 * Beziehungs-Editor für eine Zeile „{other} ist ‹…› von {viewer}". Katalog-Label →
 * direkt übernehmen (`onCommit(label, null)`, Gegenrichtung wird berechnet). „Sonstiges
 * (Freitext)" → BEIDE Richtungen erfassen und per „Übernehmen" setzen — sie lassen sich
 * nicht auseinander berechnen. `initialFwd`/`initialRev` sind die aktuell angezeigten
 * Werte („was other für viewer ist" bzw. umgekehrt).
 *
 * „Übernehmen" heißt hier: in den Entwurf legen. Geschrieben wird erst über die
 * Speichern-Leiste am Seitenende.
 */
const RelationRowEditor: React.FC<{
  initialFwd: string;
  initialRev: string;
  viewerName: string;
  otherName: string;
  busy: boolean;
  relationLabels: string[];
  /** Nachlauf hinter dem Select (z. B. „von {viewer}") — vor dem Freitext-Block. */
  trailing?: React.ReactNode;
  onCommit: (label: string, reverseLabel: string | null) => void;
}> = ({ initialFwd, initialRev, viewerName, otherName, busy, relationLabels, trailing, onCommit }) => {
  const s = useStyles();
  const isFree = useCallback(
    (v: string) => !!v.trim() && !relationLabels.includes(v.trim()),
    [relationLabels],
  );
  const [freeMode, setFreeMode] = useState(isFree(initialFwd));
  const [fwd, setFwd] = useState(initialFwd);
  const [rev, setRev] = useState(initialRev);

  // Nach externem Reload (neue gespeicherte Werte) den Editor resynchronisieren.
  useEffect(() => {
    setFreeMode(isFree(initialFwd));
    setFwd(initialFwd);
    setRev(initialRev);
  }, [initialFwd, initialRev, isFree]);

  const dirty = fwd.trim() !== initialFwd.trim() || rev.trim() !== initialRev.trim();
  const selectValue = freeMode ? RELATIONSHIP_FREE : relationLabels.includes(fwd) ? fwd : "";

  return (
    <>
      <select
        className={s.select}
        value={selectValue}
        disabled={busy}
        onChange={(e) => {
          const v = e.target.value;
          if (v === RELATIONSHIP_FREE) {
            setFreeMode(true);
            // Wechsel von einem Katalog-Wert auf Freitext → Feld leeren.
            if (!isFree(fwd)) setFwd("");
          } else {
            setFreeMode(false);
            if (v) onCommit(v, null); // Katalog: sofort speichern, Gegenrichtung wird berechnet
          }
        }}
      >
        <option value="">— wählen —</option>
        {relationLabels.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
        <option value={RELATIONSHIP_FREE}>Sonstiges (Freitext) …</option>
      </select>
      {trailing}
      {freeMode && (
        <div className={s.freeRelBox}>
          <label className={s.freeRelRow}>
            <span>{otherName} ist</span>
            <input
              className={s.freeInput}
              placeholder="z. B. Patenkind"
              value={fwd}
              disabled={busy}
              onChange={(e) => setFwd(e.target.value)}
            />
            <span>von {viewerName}</span>
          </label>
          <label className={s.freeRelRow}>
            <span>{viewerName} ist</span>
            <input
              className={s.freeInput}
              placeholder="Gegenrichtung, z. B. Patentante"
              value={rev}
              disabled={busy}
              onChange={(e) => setRev(e.target.value)}
            />
            <span>von {otherName}</span>
          </label>
          <button
            className={mergeClasses(s.addBtn, (!fwd.trim() || busy || !dirty) && s.addBtnDisabled)}
            disabled={!fwd.trim() || busy || !dirty}
            onClick={() => onCommit(fwd.trim(), rev.trim() || null)}
          >
            Übernehmen
          </button>
        </div>
      )}
    </>
  );
};

/**
 * Eine gesammelte, noch NICHT gespeicherte Beziehung. `srcId`/`destId` halten die
 * Richtung fest, in der sie eingegeben wurde (`label` = „was dest für src ist");
 * abgelegt wird sie unter einem kanonischen Paar-Schlüssel, damit dasselbe Paar aus
 * beiden Personen-Listen heraus nur EINEN Entwurf hat.
 */
interface DraftRel {
  srcId: number;
  destId: number;
  label: string;
  reverseLabel: string | null;
  /** Anzeigename der Gegenperson — nötig für Kontakte außerhalb des Clusters. */
  srcName: string;
  destName: string;
}

const pairKey = (a: number, b: number): string =>
  a < b ? `${a}|${b}` : `${b}|${a}`;

const RelationClusterDetail: React.FC<{
  contactIds: number[];
  onBack: () => void;
  /**
   * Der Kandidat, aus dem diese Ansicht geöffnet wurde — Grundlage für „als erledigt
   * markieren" (dieselbe Ausblend-Mechanik wie „keine Bereinigung nötig" in der Liste).
   * Fehlt er, entfällt das Abhaken und nur das Speichern bleibt.
   */
  dismissRef?: { kind: "name" | "relation"; refKey: string; label: string } | null;
  /** Nach erfolgreichem Abhaken — die Liste soll sich neu laden. */
  onDismissed?: () => void;
}> = ({ contactIds, onBack, dismissRef, onDismissed }) => {
  const s = useStyles();
  const relationLabels = useHouseholdRelationLabels();
  const [detail, setDetail] = useState<ClusterDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openMember, setOpenMember] = useState<Record<number, boolean>>({});
  const [openAdd, setOpenAdd] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  // „Weitere Beziehung" je Person: Add-Zeile offen + gewählte (auch externe) Person.
  const [addRelFor, setAddRelFor] = useState<Record<number, boolean>>({});
  const [addRelRef, setAddRelRef] = useState<Record<number, ContactRef | null>>({});
  // „Kontakt zum Haushalt": Person-Picker je Haushalt
  const [addDraft, setAddDraft] = useState<Record<string, ContactRef | null>>({});
  // Gesammelte Änderungen — gespeichert wird erst über die Leiste ganz unten.
  const [draft, setDraft] = useState<Record<string, DraftRel>>({});
  // Zwei Personen aus dem Cluster verbinden (Block „zwischen den Haushalten").
  const [crossOpen, setCrossOpen] = useState(false);
  const [crossA, setCrossA] = useState<number | "">("");
  const [crossB, setCrossB] = useState<number | "">("");
  // Freier Block: eine Cluster-Person + ein beliebiger Kontakt.
  const [freeA, setFreeA] = useState<number | "">("");
  const [freeRef, setFreeRef] = useState<ContactRef | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchRelationCluster(contactIds);
      setDetail(d);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setLoading(false);
    }
  }, [contactIds]);

  useEffect(() => {
    void load();
  }, [load]);

  // Flache Mitglieder-Indizes über alle Haushalte.
  const allMembers = useMemo<HouseholdMember[]>(
    () => (detail?.households ?? []).flatMap((h) => h.members),
    [detail],
  );
  const memberById = useMemo(() => {
    const map = new Map<number, HouseholdMember>();
    for (const m of allMembers) if (m.contactId != null) map.set(Number(m.contactId), m);
    return map;
  }, [allMembers]);
  const householdOf = useMemo(() => {
    const map = new Map<number, string>();
    for (const h of detail?.households ?? [])
      for (const m of h.members) if (m.contactId != null) map.set(Number(m.contactId), h.hkey);
    return map;
  }, [detail]);
  /** Anschrift je Person — unterscheidet gleichnamige Personen in den Auswahllisten. */
  const addrOfMember = useCallback(
    (id: number): string => {
      for (const h of detail?.households ?? []) {
        if (h.members.some((m) => Number(m.contactId) === id)) {
          return addrLine(h.address) || "ohne Anschrift";
        }
      }
      return "ohne Anschrift";
    },
    [detail],
  );

  const relations = detail?.relations ?? [];

  const genderOf = (id: number): "m" | "f" | null => {
    const m = memberById.get(id);
    return m ? genderKey(m.gender, m.anrede) : null;
  };

  /** Name einer Person — Cluster-Mitglied, sonst aus dem Entwurf, sonst die Id. */
  const nameOf = useCallback(
    (id: number): string => {
      const m = memberById.get(id);
      if (m) return memberFullName(m);
      for (const d of Object.values(draft)) {
        if (d.srcId === id) return d.srcName;
        if (d.destId === id) return d.destName;
      }
      return `Kontakt ${id}`;
    },
    [memberById, draft],
  );

  /** „was other für viewer ist" — GESPEICHERTER Stand, orientiert. */
  const savedLabelOf = useCallback(
    (viewerId: number, otherId: number): string => {
      const rel = relations.find(
        (r) =>
          (r.srcId === viewerId && r.destId === otherId) ||
          (r.srcId === otherId && r.destId === viewerId),
      );
      if (!rel || !rel.relationType) return "";
      if (rel.srcId === viewerId) return rel.relationType; // relationType beschreibt dest = other
      const rev = (rel.reverseRelationType ?? "").trim();
      if (rev) return rev;
      return reverseRelationLabel(rel.relationType, genderOf(otherId));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [relations, memberById],
  );

  /**
   * „was viewer für other ist" — die roh gespeicherte Gegenrichtung, orientiert. Für das
   * Vorbefüllen des Freitext-Editors (nur bei Freitext-Beziehungen relevant).
   */
  const savedReverseOf = useCallback(
    (viewerId: number, otherId: number): string => {
      const rel = relations.find(
        (r) =>
          (r.srcId === viewerId && r.destId === otherId) ||
          (r.srcId === otherId && r.destId === viewerId),
      );
      if (!rel) return "";
      // srcId=viewer: reverseRelationType ist „was viewer für other ist".
      // srcId=other: relationType (aus other-Sicht) ist „was viewer für other ist".
      return rel.srcId === viewerId
        ? (rel.reverseRelationType ?? "").trim()
        : (rel.relationType ?? "").trim();
    },
    [relations],
  );

  /**
   * Angezeigter Stand = Entwurf, falls für das Paar einer vorliegt, sonst der
   * gespeicherte. Der Entwurf ist unter dem kanonischen Paar-Schlüssel abgelegt und
   * wird hier auf die gefragte Blickrichtung gedreht.
   */
  const labelOf = useCallback(
    (viewerId: number, otherId: number): string => {
      const d = draft[pairKey(viewerId, otherId)];
      if (d) {
        if (d.srcId === viewerId) return d.label;
        return (d.reverseLabel ?? "").trim() || reverseRelationLabel(d.label, genderOf(otherId));
      }
      return savedLabelOf(viewerId, otherId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, savedLabelOf, memberById],
  );

  const reverseOf = useCallback(
    (viewerId: number, otherId: number): string => {
      const d = draft[pairKey(viewerId, otherId)];
      if (d) return d.srcId === viewerId ? (d.reverseLabel ?? "") : d.label;
      return savedReverseOf(viewerId, otherId);
    },
    [draft, savedReverseOf],
  );

  /** Ob für dieses Paar eine noch nicht gespeicherte Änderung vorliegt. */
  const isDraftPair = useCallback(
    (a: number, b: number): boolean => !!draft[pairKey(a, b)],
    [draft],
  );

  /** Alle Personen im Cluster, zu denen viewer eine Beziehung hat — inkl. Entwürfen. */
  const partnersOf = useCallback(
    (viewerId: number): number[] => {
      const set = new Set<number>();
      for (const r of relations) {
        if (r.srcId === viewerId && r.destId !== viewerId) set.add(r.destId);
        else if (r.destId === viewerId && r.srcId !== viewerId) set.add(r.srcId);
      }
      for (const d of Object.values(draft)) {
        if (d.srcId === viewerId) set.add(d.destId);
        else if (d.destId === viewerId) set.add(d.srcId);
      }
      return Array.from(set).filter((id) => memberById.has(id));
    },
    [relations, memberById, draft],
  );

  const unclearCountOf = useCallback(
    (viewerId: number): number =>
      partnersOf(viewerId).filter((o) => isUnclearLabel(labelOf(viewerId, o))).length,
    [partnersOf, labelOf],
  );

  /**
   * Eine Beziehung in den Entwurf legen (Katalog: reverseLabel=null → wird beim
   * Anzeigen/Speichern berechnet; Freitext: beide Richtungen). Gespeichert wird erst
   * über die Leiste am Seitenende.
   */
  const stageRelation = useCallback(
    (
      viewerId: number,
      otherId: number,
      label: string,
      reverseLabel: string | null = null,
      names?: { viewer?: string; other?: string },
    ) => {
      if (!label.trim() || !otherId || viewerId === otherId) return;
      setDraft((prev) => ({
        ...prev,
        [pairKey(viewerId, otherId)]: {
          srcId: viewerId,
          destId: otherId,
          label: label.trim(),
          reverseLabel: reverseLabel?.trim() ? reverseLabel.trim() : null,
          srcName: names?.viewer ?? nameOf(viewerId),
          destName: names?.other ?? nameOf(otherId),
        },
      }));
    },
    [nameOf],
  );

  const dropDraft = (key: string) =>
    setDraft((prev) => {
      const { [key]: _drop, ...rest } = prev;
      return rest;
    });

  /** Weitere Beziehung für `viewerId` zu einer (auch externen) Person vormerken. */
  const addRelationFor = (
    viewerId: number,
    otherId: number,
    label: string,
    reverseLabel: string | null,
    otherName?: string,
  ) => {
    if (!otherId || otherId === viewerId || !label.trim()) return;
    stageRelation(viewerId, otherId, label, reverseLabel, { other: otherName });
    setAddRelFor((p) => ({ ...p, [viewerId]: false }));
    setAddRelRef((p) => ({ ...p, [viewerId]: null }));
  };

  const draftList = useMemo(() => Object.entries(draft), [draft]);

  /** Entwürfe ablegen; optional den Kandidaten anschließend abhaken. */
  const saveAll = async (alsoDismiss: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await upsertClusterRelations(
        draftList.map(([, d]) => ({
          srcId: d.srcId,
          destId: d.destId,
          label: d.label,
          reverseLabel: d.reverseLabel,
        })),
      );
      setDraft({});
      if (alsoDismiss && dismissRef) {
        await dismissCandidate(dismissRef.kind, dismissRef.refKey, dismissRef.label);
        onDismissed?.();
        onBack();
        return;
      }
      await load();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  /** Nur abhaken — ohne die (noch offenen) Entwürfe zu speichern. */
  const dismissOnly = async () => {
    if (!dismissRef) return;
    setBusy(true);
    setError(null);
    try {
      await dismissCandidate(dismissRef.kind, dismissRef.refKey, dismissRef.label);
      onDismissed?.();
      onBack();
    } catch (e: any) {
      setError(String(e?.message ?? e));
      setBusy(false);
    }
  };

  /** Kontakt als echtes Mitglied zum Haushalt hinzufügen. */
  const addMemberToHousehold = async (h: ClusterHousehold) => {
    const ref = addDraft[h.hkey];
    if (!ref?.contactId) return;
    setBusy(true);
    setError(null);
    try {
      await addClusterMember(h.addrKey, Number(ref.contactId));
      setOpenAdd((p) => ({ ...p, [h.hkey]: false }));
      setAddDraft((p) => ({ ...p, [h.hkey]: null }));
      await load();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  // Cross-Household-Beziehungen (zwischen verschiedenen Haushalten), kanonisch je Paar.
  const crossRelations = useMemo(() => {
    const seen = new Set<string>();
    const out: HouseholdRelationRow[] = [];
    for (const r of relations) {
      const hs = householdOf.get(r.srcId);
      const hd = householdOf.get(r.destId);
      if (!hs || !hd || hs === hd) continue;
      const key = [r.srcId, r.destId].sort((a, b) => a - b).join("|");
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
    return out;
  }, [relations, householdOf]);

  if (loading) {
    return (
      <div className={s.root}>
        <button className={s.backBtn} onClick={onBack}>
          <ArrowLeft20Regular /> Zurück zur Cluster-Liste
        </button>
        <div className={s.loading}>Lade Cluster…</div>
      </div>
    );
  }

  const households = detail?.households ?? [];
  const clusterLabel =
    Array.from(new Set(allMembers.map((m) => m.name).filter(Boolean))).sort().join(" / ") ||
    "Haushalt";

  return (
    <div className={s.root}>
      <button className={s.backBtn} onClick={onBack}>
        <ArrowLeft20Regular /> Zurück zur Cluster-Liste
      </button>

      <div className={s.head}>
        <span className={s.title}>{clusterLabel}</span>
        <span className={s.sub}>
          {households.length} {households.length === 1 ? "Haushalt" : "Haushalte"} ·{" "}
          {allMembers.length} Personen · Beziehungen innerhalb des Clusters klären
        </span>
      </div>

      {error && <div className={s.err}>{error}</div>}

      {/* Haushalte mit ausklappbaren Personen */}
      {households.map((h: ClusterHousehold) => (
        <div key={h.hkey} className={s.hhCard}>
          <div className={s.hhHead}>
            <span className={s.hhIcon}>
              <Home20Regular />
            </span>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span className={s.hhTitle}>{addrLine(h.address) || "Einzelperson (ohne Anschrift)"}</span>
              <span className={s.hhSub}>
                {h.members.length} {h.members.length === 1 ? "Person" : "Personen"}
              </span>
            </div>
          </div>

          {h.members.map((m) => {
            const id = Number(m.contactId);
            const partners = partnersOf(id);
            const open = !!openMember[id];
            const unclear = unclearCountOf(id);
            return (
              <div key={id} className={s.memberRow}>
                <button
                  className={s.memberHead}
                  onClick={() => setOpenMember((p) => ({ ...p, [id]: !p[id] }))}
                >
                  {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                  <Person20Regular style={{ color: BRAND.muted }} />
                  <span className={s.memberName}>{memberFullName(m)}</span>
                  {unclear > 0 && (
                    <span className={s.unclearDot}>
                      <Warning16Regular /> {unclear} zu klären
                    </span>
                  )}
                  <span className={s.relCountChip}>
                    {partners.length} {partners.length === 1 ? "Beziehung" : "Beziehungen"}
                  </span>
                </button>

                {open && (
                  <div className={s.relList}>
                    {partners.length === 0 && (
                      <span className={s.emptyRel}>Keine erfassten Beziehungen zu anderen Personen.</span>
                    )}
                    {partners.map((otherId) => {
                      const other = memberById.get(otherId);
                      const cur = labelOf(id, otherId);
                      const uncl = isUnclearLabel(cur);
                      const otherNm = other ? memberFullName(other) : String(otherId);
                      return (
                        <div key={otherId} className={s.relRow}>
                          <span className={s.relOther}>{otherNm}</span>
                          <span className={s.relIs}>ist</span>
                          <RelationRowEditor
                            initialFwd={cur}
                            initialRev={reverseOf(id, otherId)}
                            viewerName={memberFullName(m)}
                            otherName={otherNm}
                            busy={busy}
                            relationLabels={relationLabels}
                            trailing={<span className={s.relFromTo}>von {memberFullName(m)}</span>}
                            onCommit={(l, r) => stageRelation(id, otherId, l, r)}
                          />
                          {isDraftPair(id, otherId) && (
                            <span className={s.draftChip}>ungespeichert</span>
                          )}
                          {uncl && (
                            <span className={s.unclearDot}>
                              <Warning16Regular /> {cur ? `„${cur}"` : "ohne Label"}
                            </span>
                          )}
                        </div>
                      );
                    })}

                    {/* Weitere Beziehung für diese Person — auch zu Kontakten außerhalb des Clusters. */}
                    {!addRelFor[id] ? (
                      <button
                        className={s.miniAddBtn}
                        onClick={() => setAddRelFor((p) => ({ ...p, [id]: true }))}
                      >
                        <Add20Regular style={{ fontSize: 16 }} /> weitere Beziehung
                      </button>
                    ) : (
                      <div className={s.addInline}>
                        <div className={s.addInlinePicker}>
                          <ContactRefField
                            wantType="Person"
                            personOnly
                            idBase={`cl-rel-${id}`}
                            value={addRelRef[id] ?? null}
                            onChange={(v) => setAddRelRef((p) => ({ ...p, [id]: v }))}
                          />
                        </div>
                        {addRelRef[id]?.contactId ? (
                          <>
                            <span className={s.relIs}>ist</span>
                            <RelationRowEditor
                              initialFwd=""
                              initialRev=""
                              viewerName={memberFullName(m)}
                              otherName={refName(addRelRef[id])}
                              busy={busy}
                              relationLabels={relationLabels}
                              trailing={<span className={s.relFromTo}>von {memberFullName(m)}</span>}
                              onCommit={(l, r) =>
                                addRelationFor(
                                  id,
                                  Number(addRelRef[id]!.contactId),
                                  l,
                                  r,
                                  refName(addRelRef[id]),
                                )
                              }
                            />
                          </>
                        ) : null}
                        <button
                          className={s.miniAddBtn}
                          onClick={() => {
                            setAddRelFor((p) => ({ ...p, [id]: false }));
                            setAddRelRef((p) => ({ ...p, [id]: null }));
                          }}
                        >
                          Abbrechen
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Kontakt zum Haushalt hinzufügen — eingeklappt (nur bei echter Anschrift). */}
          {!h.addrKey.startsWith("contact:") && (
            <div className={s.addWrap}>
              {!openAdd[h.hkey] ? (
                <button
                  className={s.toggleBtn}
                  onClick={() => setOpenAdd((p) => ({ ...p, [h.hkey]: true }))}
                >
                  <Add20Regular /> Kontakt zum Haushalt hinzufügen
                </button>
              ) : (
                <div className={s.addInline}>
                  <div className={s.addInlinePicker}>
                    <ContactRefField
                      wantType="Person"
                      personOnly
                      idBase={`cl-add-${h.hkey}`}
                      value={addDraft[h.hkey] ?? null}
                      onChange={(v) => setAddDraft((p) => ({ ...p, [h.hkey]: v }))}
                    />
                  </div>
                  <button
                    className={mergeClasses(s.addBtn, !addDraft[h.hkey]?.contactId && s.addBtnDisabled)}
                    disabled={!addDraft[h.hkey]?.contactId || busy}
                    onClick={() => addMemberToHousehold(h)}
                  >
                    <Add20Regular style={{ fontSize: 16 }} /> Zum Haushalt hinzufügen
                  </button>
                  <button
                    className={s.miniAddBtn}
                    onClick={() => {
                      setOpenAdd((p) => ({ ...p, [h.hkey]: false }));
                      setAddDraft((p) => ({ ...p, [h.hkey]: null }));
                    }}
                  >
                    Abbrechen
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Beziehungen zwischen den Haushalten */}
      {households.length > 1 && (
        <div className={s.sectionBox}>
          <span className={s.sectionTitle}>Beziehungen zwischen den Haushalten</span>
          <span className={s.sectionHint}>
            Diese Beziehungen verbinden die Haushalte im Cluster — hier direkt präzisieren.
          </span>
          {crossRelations.length === 0 && (
            <span className={s.emptyRel}>Keine haushaltsübergreifenden Beziehungen.</span>
          )}
          {crossRelations.map((r) => {
            const a = memberById.get(r.srcId);
            const b = memberById.get(r.destId);
            const cur = labelOf(r.srcId, r.destId); // was dest für src ist
            const srcNm = a ? memberFullName(a) : String(r.srcId);
            const destNm = b ? memberFullName(b) : String(r.destId);
            return (
              <div key={`${r.srcId}|${r.destId}`} className={s.crossRow}>
                <span className={s.relOther}>{destNm}</span>
                <span className={s.relIs}>ist</span>
                <RelationRowEditor
                  initialFwd={cur}
                  initialRev={reverseOf(r.srcId, r.destId)}
                  viewerName={srcNm}
                  otherName={destNm}
                  busy={busy}
                  relationLabels={relationLabels}
                  trailing={<span className={s.relFromTo}>von {srcNm}</span>}
                  onCommit={(l, rev) => stageRelation(r.srcId, r.destId, l, rev)}
                />
                {isDraftPair(r.srcId, r.destId) && (
                  <span className={s.draftChip}>ungespeichert</span>
                )}
                {isUnclearLabel(cur) && (
                  <span className={s.unclearDot}>
                    <Warning16Regular /> {cur ? `„${cur}"` : "ohne Label"}
                  </span>
                )}
              </div>
            );
          })}

          {/* Weitere haushaltsübergreifende Beziehung: zwei Personen aus dem Cluster. */}
          {!crossOpen ? (
            <button className={s.miniAddBtn} onClick={() => setCrossOpen(true)}>
              <Add20Regular style={{ fontSize: 16 }} /> weitere Beziehung zwischen den Haushalten
            </button>
          ) : (
            <div className={s.addInline}>
              <select
                className={s.personSelect}
                value={crossA}
                onChange={(e) => setCrossA(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">— Person wählen —</option>
                {allMembers.map((m) => (
                  <option key={Number(m.contactId)} value={Number(m.contactId)}>
                    {memberFullName(m)}
                    {householdOf.get(Number(m.contactId))
                      ? ` · ${addrOfMember(Number(m.contactId))}`
                      : ""}
                  </option>
                ))}
              </select>
              <span className={s.relIs}>und</span>
              <select
                className={s.personSelect}
                value={crossB}
                onChange={(e) => setCrossB(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">— Person wählen —</option>
                {allMembers
                  .filter((m) => Number(m.contactId) !== crossA)
                  .map((m) => (
                    <option key={Number(m.contactId)} value={Number(m.contactId)}>
                      {memberFullName(m)}
                      {householdOf.get(Number(m.contactId))
                        ? ` · ${addrOfMember(Number(m.contactId))}`
                        : ""}
                    </option>
                  ))}
              </select>
              {crossA !== "" && crossB !== "" && (
                <div className={s.freeBlockRow}>
                  <span className={s.relOther}>{nameOf(Number(crossB))}</span>
                  <span className={s.relIs}>ist</span>
                  <RelationRowEditor
                    initialFwd={labelOf(Number(crossA), Number(crossB))}
                    initialRev={reverseOf(Number(crossA), Number(crossB))}
                    viewerName={nameOf(Number(crossA))}
                    otherName={nameOf(Number(crossB))}
                    busy={busy}
                    relationLabels={relationLabels}
                    trailing={
                      <span className={s.relFromTo}>von {nameOf(Number(crossA))}</span>
                    }
                    onCommit={(l, rev) => {
                      stageRelation(Number(crossA), Number(crossB), l, rev);
                      setCrossA("");
                      setCrossB("");
                      setCrossOpen(false);
                    }}
                  />
                </div>
              )}
              <button
                className={s.miniAddBtn}
                onClick={() => {
                  setCrossOpen(false);
                  setCrossA("");
                  setCrossB("");
                }}
              >
                Abbrechen
              </button>
            </div>
          )}
        </div>
      )}

      {/* Freier Block: eine Person aus dem Cluster mit einem beliebigen Kontakt verbinden. */}
      <div className={s.sectionBox}>
        <span className={s.sectionTitle}>Weitere Beziehungen anlegen</span>
        <span className={s.sectionHint}>
          Person aus diesem Cluster wählen und mit einem beliebigen Kontakt verbinden —
          auch mit Personen außerhalb der Haushalte. Die Beziehung wird vorgemerkt und
          erst mit „Speichern" abgelegt.
        </span>
        <div className={s.addInline}>
          <select
            className={s.personSelect}
            value={freeA}
            onChange={(e) => setFreeA(e.target.value ? Number(e.target.value) : "")}
          >
            <option value="">— Person aus dem Cluster —</option>
            {allMembers.map((m) => (
              <option key={Number(m.contactId)} value={Number(m.contactId)}>
                {memberFullName(m)} · {addrOfMember(Number(m.contactId))}
              </option>
            ))}
          </select>
          <div className={s.addInlinePicker}>
            <ContactRefField
              wantType="Person"
              personOnly
              idBase="cl-free-rel"
              value={freeRef}
              onChange={setFreeRef}
            />
          </div>
          {freeA !== "" && freeRef?.contactId ? (
            <div className={s.freeBlockRow}>
              <span className={s.relOther}>{refName(freeRef)}</span>
              <span className={s.relIs}>ist</span>
              <RelationRowEditor
                initialFwd={labelOf(Number(freeA), Number(freeRef.contactId))}
                initialRev={reverseOf(Number(freeA), Number(freeRef.contactId))}
                viewerName={nameOf(Number(freeA))}
                otherName={refName(freeRef)}
                busy={busy}
                relationLabels={relationLabels}
                trailing={<span className={s.relFromTo}>von {nameOf(Number(freeA))}</span>}
                onCommit={(l, rev) => {
                  stageRelation(Number(freeA), Number(freeRef.contactId), l, rev, {
                    other: refName(freeRef),
                  });
                  setFreeRef(null);
                }}
              />
            </div>
          ) : (
            <span className={s.sectionHint}>
              Beide Seiten wählen, dann erscheint die Beziehungsauswahl.
            </span>
          )}
        </div>
      </div>

      {/* Gesammelte, noch nicht gespeicherte Änderungen */}
      {draftList.length > 0 && (
        <div className={s.sectionBox}>
          <span className={s.sectionTitle}>
            Geplante Änderungen ({draftList.length})
          </span>
          {draftList.map(([key, d]) => (
            <div key={key} className={s.changeRow}>
              <span className={s.changeText}>
                {d.destName} ist <strong>{d.label}</strong> von {d.srcName}
                {d.reverseLabel ? ` · ${d.srcName} ist ${d.reverseLabel} von ${d.destName}` : ""}
              </span>
              <button className={s.linkBtn} onClick={() => dropDraft(key)} disabled={busy}>
                verwerfen
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Speichern / abhaken */}
      <div className={s.actionBar}>
        <span className={s.actionInfo}>
          {draftList.length === 0
            ? "Keine offenen Änderungen."
            : `${draftList.length} ${draftList.length === 1 ? "Änderung" : "Änderungen"} noch nicht gespeichert.`}
        </span>
        <button
          className={mergeClasses(s.addBtn, (busy || draftList.length === 0) && s.addBtnDisabled)}
          disabled={busy || draftList.length === 0}
          onClick={() => void saveAll(!!dismissRef)}
          title={
            dismissRef
              ? "Änderungen speichern und den Kandidaten als erledigt markieren"
              : "Änderungen speichern"
          }
        >
          <Save20Regular style={{ fontSize: 16 }} />
          {dismissRef ? "Speichern & erledigt" : "Speichern"}
        </button>
        {dismissRef && (
          <button
            className={mergeClasses(s.ghostBtn, busy && s.addBtnDisabled)}
            disabled={busy}
            onClick={() => void dismissOnly()}
            title={'Ohne weitere Änderungen aus der Worklist nehmen (unter „Ausgeblendete“ wiederherstellbar)'}
          >
            <CheckmarkCircle20Regular style={{ fontSize: 16 }} /> Als erledigt markieren
          </button>
        )}
        {draftList.length > 0 && (
          <button className={s.linkBtn} onClick={() => setDraft({})} disabled={busy}>
            alle verwerfen
          </button>
        )}
      </div>
    </div>
  );
};

export default RelationClusterDetail;
